import React, { Component } from 'react'
import { getValidationSchema } from './InvoiceFormValidationSchema'
import { observer, inject } from 'mobx-react'
import TextArea from '@components/Form/TextArea'
import Checkbox from '@components/Form/Checkbox'
import Button from '@components/Button'
import FormLoader from '@components/FormLoader'
import FormError from '@components/FormError'
import Tooltip from '@components/Tooltip'
import FieldPair from '@components/Form/FieldPair'
import DatePicker from '@components/Form/DatePicker'
import { invoiceApi } from '@atheneum/api'
import { Formik, Form, Field } from 'formik'
import { Autocomplete, Dropdown } from '@components/Form/Autocomplete'
import Input from '@components/Form/Input'
import PropTypes from 'prop-types'
import { generateInvoiceUrl } from '@atheneum/api/dist/lib'
import { withRouter } from 'react-router-dom'

class InvoiceForm extends Component {
  static propTypes = {
    invoiceStore: PropTypes.object,
    invoice: PropTypes.object,
    contract: PropTypes.object,
    project: PropTypes.object,
    modalStore: PropTypes.object,
    sidebarStore: PropTypes.object,
    staticData: PropTypes.object,
    atheneumOfficeStore: PropTypes.object,
    title: PropTypes.string,
    isEdit: PropTypes.bool,
    isCreditNote: PropTypes.bool,
    invoiceType: PropTypes.number.isRequired,
    history: PropTypes.object
  }

  static defaultProps = {
    hideButtons: true,
    showCloseIcon: true,
    shouldCloseOnOverlayClick: true,
    isEdit: false,
    isCreditNote: false
  }

  state = {
    error: null
  }

  constructor(props) {
    super(props)
    const {
      isEdit,
      invoiceStore,
      project,
      staticData,
      invoiceType,
      contract,
      isCreditNote
    } = props

    // this is a bad pattern, remove this
    if (!isCreditNote && !isEdit) {
      invoiceStore.clearInvoice()
    }

    const isPayg = invoiceType === staticData.CONST.INVOICE_TYPES.PAYG
    const isNonPayg = invoiceType === staticData.CONST.INVOICE_TYPES.NON_PAYG

    if (isPayg) {
      invoiceStore.getInvoicingEntities(project)
    } else if (isNonPayg) {
      invoiceStore.getInvoicingEntities(contract)
    }
  }

  isFieldDisabled = fieldName => {
    const { invoice, staticData, isEdit, isCreditNote } = this.props
    const { INVOICE_STATUSES } = staticData.CONST

    if (!invoice) return false

    const isInvoiceEditForm = !isCreditNote && !isEdit
    if (isInvoiceEditForm) return false

    const isCommentInCreditNoteCreateForm =
      isCreditNote && !isEdit && fieldName === 'comment'
    if (isCommentInCreditNoteCreateForm) return false

    if (isCreditNote) return true

    return invoice.invoiceStatusId >= INVOICE_STATUSES.APPROVED
  }

  openInvoicingEntitySidebar = invoicingEntityId => {
    if (!invoicingEntityId) return

    this.props.sidebarStore.openSidebar({
      template: 'invoicing-entity',
      id: invoicingEntityId
    })
  }

  onInvoicingEntityChange = (invoicingEntity, form) => {
    form.setFieldValue('invoicingEntity', invoicingEntity, false)
    form.setFieldTouched('invoicingEntity', true, true)

    if (!invoicingEntity) return
    form.setFieldValue('invoicingEntityId', invoicingEntity.id, false)
  }

  onAtheneumOfficeChange = (atheneumOffice, form) => {
    form.setFieldValue('atheneumOffice', atheneumOffice, false)
    form.setFieldTouched('atheneumOffice', true, true)

    if (!atheneumOffice) return
    form.setFieldValue('atheneumOfficeId', atheneumOffice.id, false)
  }

  onCurrencyChange = (currency, form) => {
    form.setFieldValue('currency', currency, false)
    form.setFieldTouched('currency', true, true)

    if (!currency) return
    form.setFieldValue('currencyId', currency.id, false)
  }

  saveInvoice = invoiceData => {
    const { invoiceStore } = this.props
    const { id, fees } = invoiceStore.invoice
    const data = {
      id,
      ...invoiceData,
      fees: fees.map(fee => ({ id: fee.id }))
    }

    return invoiceApi.updateInvoice(data)
  }

  submitForm = (invoiceData, actions) => {
    const {
      isEdit,
      invoiceStore,
      modalStore,
      invoiceType,
      staticData,
      isCreditNote,
      invoice,
      contract
    } = this.props
    const { INVOICE_TYPES } = staticData.CONST
    const isPayg = invoiceType === INVOICE_TYPES.PAYG
    const isNonPayg = invoiceType === INVOICE_TYPES.NON_PAYG

    if (isCreditNote && !isEdit) {
      return this.createCreditNote(invoice, invoiceData, actions)
    }

    const isNonPaygCreateForm = !isEdit && isNonPayg
    if (isNonPaygCreateForm) {
      modalStore.close()
      return modalStore.show({
        type: 'INVOICE_ITEMS_FORM',
        props: {
          title: 'Add invoice items',
          cancelText: 'Cancel',
          okText: 'Add to invoice',
          invoice: invoiceData,
          contract
        }
      })
    }

    const isPaygCreateForm = !isEdit && isPayg
    if (isPaygCreateForm) {
      modalStore.close()
      return modalStore.show({
        type: 'INVOICE_ADD_FEES_FORM',
        props: {
          title: 'Add fees',
          cancelText: 'Cancel',
          okText: 'Add to invoice',
          invoice: invoiceData
        }
      })
    }

    return this.saveInvoice(invoiceData)
      .then(savedInvoice => {
        invoiceStore.get(savedInvoice.id)
        modalStore.close()
      })
      .catch(({ message }) => {
        this.setState({ error: message })
        actions.setSubmitting(false)
      })
  }

  createCreditNote = (invoice, invoiceData, actions) => {
    const { invoiceStore, modalStore, history } = this.props

    return invoiceStore
      .createCreditNote(invoice, {
        comment: invoiceData.comment,
        internalRemarks: invoiceData.internalRemarks,
        isWriteOff: invoiceData.isWriteOff
      })
      .then(savedInvoice => {
        // use invoice data, savedInvoice only contains ID
        const invoiceUrl = generateInvoiceUrl({
          invoiceId: savedInvoice.id,
          projectId: invoice.projectId,
          contractId: invoice.contractId
        })
        history.push(invoiceUrl)
        // invoiceStore.get(savedInvoice.id)
        modalStore.close()
      })
      .catch(({ message }) => {
        this.setState({ error: message })
        actions.setSubmitting(false)
      })
  }

  renderInvoicingEntity = invoicingEntity => {
    // Show nothing when no inv. entity selected
    if (!invoicingEntity || !invoicingEntity.id) return ''

    const { city, countryId } = invoicingEntity.address
    const countryNames = this.props.staticData.names.countries
    const country = countryNames[countryId]

    const name = [invoicingEntity.legalName, city, country]
      .filter(namePart => namePart != null)
      .join(', ')

    return name
  }

  renderDropdownItem = item => item && item.name

  render() {
    const {
      title,
      invoiceStore,
      modalStore,
      staticData,
      contract,
      project,
      atheneumOfficeStore,
      invoiceType,
      invoice,
      isEdit,
      isCreditNote
    } = this.props
    const currencies = staticData.currencies

    const { INVOICE_TYPES } = staticData.CONST
    const isNonPayg = invoiceType === INVOICE_TYPES.NON_PAYG

    return (
      <Formik
        initialValues={initializeForm({
          staticData,
          invoice,
          contract,
          project,
          invoiceType,
          isEdit,
          isCreditNote,
          atheneumOfficeStore
        })}
        validationSchema={getValidationSchema(staticData, isCreditNote)}
        onSubmit={this.submitForm}
        render={form => {
          const commentLabel = (
            <div>
              {isCreditNote ? 'Reason for credit note' : 'Comment'}
              <Tooltip
                tooltip={
                  'Notes and Payment Conditions mentioned here will be printed on the Invoice.'
                }
              />
            </div>
          )

          const hideExpertNameLabel = (
            <div className='expert-form__label'>
              Hide expert name
              <Tooltip
                tooltip={
                  'Checking this will prevent expert names from appearing in the Invoice view under "Invoice items", as well as in "Invoice items" of the PDF export. Once an Invoice is created, this value cannot be changed.'
                }
              />
            </div>
          )

          return (
            <div className='expert-form invoice-form'>
              <FormLoader loading={form.isSubmitting} />
              <h1 className='expert-form__title'>{title}</h1>
              <FormError error={this.state.error} />

              <Form onSubmit={form.handleSubmit}>
                <div className='expert-form__section'>
                  <Field
                    data-cy='invoicing-entity-autocomplete'
                    disabled={this.isFieldDisabled()}
                    showResultsOnEmptyInput
                    name='invoicingEntity'
                    label='Invoicing entity'
                    placeholder='Select invoicing entity'
                    onChange={invoicingEntity =>
                      this.onInvoicingEntityChange(invoicingEntity, form)
                    }
                    itemToString={this.renderInvoicingEntity}
                    options={invoiceStore.invoicingEntities}
                    component={Autocomplete}
                    linkCallback={() =>
                      this.openInvoicingEntitySidebar(
                        form.values.invoicingEntityId
                      )
                    }
                    linkLabel='DETAILS'
                  />

                  <Field
                    data-cy='atheneum-office-dropdown'
                    disabled={this.isFieldDisabled()}
                    name='atheneumOffice'
                    label='Atheneum office'
                    placeholder='Select atheneum office'
                    itemToString={this.renderDropdownItem}
                    options={atheneumOfficeStore.atheneumOffices}
                    onChange={atheneumOffice =>
                      this.onAtheneumOfficeChange(atheneumOffice, form)
                    }
                    component={Dropdown}
                  />

                  <Field
                    disabled={this.isFieldDisabled()}
                    name='vat'
                    label='VAT (%)'
                    component={Input}
                  />

                  <Field
                    data-cy='currency-dropdown'
                    disabled={this.isFieldDisabled()}
                    name='currency'
                    label='Currency'
                    placeholder='Select currency'
                    itemToString={this.renderDropdownItem}
                    options={currencies}
                    onChange={currency => this.onCurrencyChange(currency, form)}
                    component={Dropdown}
                  />

                  {isNonPayg && (
                    <FieldPair
                      separator
                      form={form}
                      label='Period of service'
                      fieldAProps={{
                        disabled: this.isFieldDisabled(),
                        noError: true,
                        dateOnly: true,
                        name: 'startDate',
                        date: form.values.startDate,
                        maxDate: form.values.endDate,
                        placeholder: 'from'
                      }}
                      fieldBProps={{
                        disabled: this.isFieldDisabled(),
                        noError: true,
                        dateOnly: true,
                        name: 'endDate',
                        date: form.values.endDate,
                        minDate: form.values.startDate,
                        placeholder: 'to'
                      }}
                      componentA={DatePicker}
                      componentB={DatePicker}
                    />
                  )}

                  <Field
                    disabled={this.isFieldDisabled()}
                    name='caseCode'
                    label='Case code'
                    component={Input}
                  />

                  <Field
                    disabled={this.isFieldDisabled('comment')}
                    name='comment'
                    label={commentLabel}
                    component={TextArea}
                  />

                  <Field
                    name='internalRemarks'
                    label='Internal remarks'
                    component={TextArea}
                  />

                  {isCreditNote && (
                    <Field
                      name='isWriteOff'
                      label={
                        <div className='expert-form__label'>Write-off</div>
                      }
                      component={Checkbox}
                      className={isNonPayg ? 'm-b-xl' : ''}
                    />
                  )}

                  <Field
                    name='hideExpertName'
                    className='hide-expert-name-field'
                    label={hideExpertNameLabel}
                    component={Checkbox}
                    disabled={this.isFieldDisabled()}
                  />
                </div>
                <div className='expert-form__section p-b-0'>
                  <Button primary submit disabled={form.isSubmitting}>
                    Save
                  </Button>
                  <Button onClick={modalStore.close}>Cancel</Button>
                </div>
              </Form>
            </div>
          )
        }}
      />
    )
  }
}

function getOffice(project, offices) {
  const base = { id: null, name: null };
  const LATAM_COUNTRY_INVOICING_REGION_ID = 3;

  if (!(project && project.office.address.country.invoicingRegionId === LATAM_COUNTRY_INVOICING_REGION_ID))
    return base;

  const ATHENEUM_SANTIAGO_OFFICE_ID = 3;
  const office = offices.find(x => x.id === ATHENEUM_SANTIAGO_OFFICE_ID);

  if (!office)
    return base;

  return {
    id: office.id,
    name: office.name
  };
}

function initializeForm({
  staticData,
  contract,
  project,
  invoice,
  invoiceType,
  isEdit,
  isCreditNote,
  atheneumOfficeStore
}) {
  const isPayg = invoiceType === staticData.CONST.INVOICE_TYPES.PAYG
  const isNonPayg = invoiceType === staticData.CONST.INVOICE_TYPES.NON_PAYG

  if (!isEdit && !isCreditNote) {
    return {
      invoiceTypeId: invoiceType,
      projectId: isPayg ? project.id : null,
      contractId: isNonPayg ? contract.id : null,
      caseCode: isPayg ? project.caseCode : null,
      invoicingEntity: { id: null, legalName: null },
      invoicingEntityId: null,
      atheneumOffice: isPayg ? getOffice(project, atheneumOfficeStore.atheneumOffices) : { id: null, name: null },
      atheneumOfficeId: isPayg ? getOffice(project, atheneumOfficeStore.atheneumOffices).id : null,
      currency: { id: null, name: null },
      currencyId: null,
      vat: null,
      comment: '',
      internalRemarks: '',
      startDate: isNonPayg ? contract.startDate : null,
      endDate: isNonPayg ? contract.endDate : null,
      isWriteOff: false,
      hideExpertName: true
    }
  }

  const currency = staticData.currencies.find(c => c.id === invoice.currencyId)

  return {
    invoiceTypeId: invoice.invoiceTypeId,
    caseCode: invoice.caseCode,
    invoicingEntity: invoice.invoicingEntity,
    invoicingEntityId: invoice.invoicingEntityId,
    atheneumOffice: invoice.atheneumOffice,
    atheneumOfficeId: invoice.atheneumOfficeId,
    currency: currency,
    currencyId: invoice.currencyId,
    vat: invoice.vat,
    comment: isEdit ? invoice.comment : '',
    internalRemarks: invoice.internalRemarks,
    startDate: invoice.startDate,
    endDate: invoice.endDate,
    isWriteOff: isEdit ? Boolean(invoice.isWriteOff) : false,
    hideExpertName: Boolean(invoice.hideExpertName)
  }
}

const ObservableInvoiceForm = inject(
  'invoiceStore',
  'staticData',
  'modalStore',
  'sidebarStore',
  'atheneumOfficeStore'
)(observer(InvoiceForm))

export default withRouter(ObservableInvoiceForm)
